/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { MediaSterilization } from "../../../types/mediaBatchSterilization.types";
import { SterilizationDetails } from "../../../types/mediaBatchCommon.types";

interface Props {
	sterilizationPayload: MediaSterilization;
	handlePayloadChange: (field: string, value: string | number) => void;
	targetTemp?: string;
	sterilizationDetails?: SterilizationDetails;
	isReadOnly?: boolean;
	errors: {
		cycleStart: string;
		targetTime: string;
		retainedDuration: string;
		pressureRelease: string;
	};
	setErrors: React.Dispatch<React.SetStateAction<any>>;
}

const SterilizationProcessEvents = ({
	sterilizationPayload,
	handlePayloadChange,
	targetTemp,
	sterilizationDetails,
	isReadOnly = false,
	errors,
	setErrors
	
}: Props) => {
	const { t } = useTranslation();
	const [isProcessDeviation, setIsProcessDeviation] = useState(false);

	// Hydrate form fields if data exists in read-only mode or initial load
	useEffect(() => {
		if (!sterilizationDetails) return;

		const formatDateTimeLocal = (value: string) => {
			if (!value) return "";

			// "21-07-2026 09:38:00"
			const [date, time] = value.split(" ");
			const [day, month, year] = date.split("-");

			return `${year}-${month}-${day}T${time.slice(0, 5)}`;
		};

		const formatTime = (value: string) => {
			if (!value) return "";
			return value.slice(0, 5);
		};

		handlePayloadChange(
			"cycle_start_datetime",
			formatDateTimeLocal(sterilizationDetails.cycle_start_datetime)
		);

		handlePayloadChange(
			"time_target_temp_attained",
			formatTime(sterilizationDetails.target_temp_attained_time)
		);

		handlePayloadChange(
			"retained_duration_min",
			Number(sterilizationDetails.retained_duration_min)
		);

		handlePayloadChange(
			"pressure_release_time",
			formatTime(sterilizationDetails.pressure_release_time)
		);
	}, [sterilizationDetails]);

	const inputClassName = `w-full px-4 py-3 border rounded-xl text-xs font-bold outline-none transition-all
		${isReadOnly
			? "bg-slate-100 border-slate-200 text-slate-500 cursor-not-allowed opacity-70 disabled:bg-slate-100 disabled:text-slate-500 disabled:border-slate-200"
			: "bg-[#F9FAFB] border-slate-100 text-slate-700 focus:border-orange-500 focus:bg-white"
		}`;

	return (
		<div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden flex flex-col h-full">
			<div className="px-6 py-2 border-b border-slate-100 flex items-center gap-2">
				<div className="w-6 h-6 rounded-md bg-[#2D3E50] text-white flex items-center justify-center text-[10px] font-black">
					03
				</div>
				<h3 className="text-[10px] font-black text-[#2D3E50] uppercase tracking-widest mt-2">
					{t("mediaBatchInit.sterilization.processEvents")}
				</h3>
			</div>
			<div className="p-6 space-y-8 flex-1">
				<div className="space-y-2">
					<label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">
						{t("mediaBatchInit.sterilization.cycleStartAndEndTime")}  <span className="text-red-500">*</span>
					</label>
					<input
						value={sterilizationPayload.cycle_start_datetime}
						onChange={(e) => { handlePayloadChange("cycle_start_datetime", e.target.value); setErrors((prev: any) => ({ ...prev, cycleStart: "" })); }}
						type="datetime-local"
						disabled={isReadOnly}
						className={`${inputClassName} disabled:appearance-none`}
					/>
					{!isReadOnly && errors.cycleStart && <p className="text-red-500 text-[11px]">{errors.cycleStart}</p>}
				</div>
				<div className="space-y-2">
					<div className="flex justify-between items-baseline">
						<label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">
							{t("mediaBatchInit.sterilization.timeAttained", { temp: targetTemp })}  <span className="text-red-500">*</span>
						</label>
						<span className="text-[8px] font-black text-orange-500 bg-orange-50 px-2 py-0.5 rounded tracking-tighter">
							{t("mediaBatchInit.sterilization.criticalNode")}
						</span>
					</div>
					<input
						type="time"
						value={sterilizationPayload.time_target_temp_attained}
						onChange={(e) => { handlePayloadChange("time_target_temp_attained", e.target.value); setErrors((prev: any) => ({ ...prev, targetTime: "" })); }}
						disabled={isReadOnly}
						className={`${inputClassName} disabled:appearance-none`}
					/>
					{!isReadOnly && errors.targetTime && <p className="text-red-500 text-[11px]">{errors.targetTime}</p>}
				</div>
				<div className="space-y-2">
					<label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">
						{t("mediaBatchInit.sterilization.retainedDuration")}  <span className="text-red-500">*</span>
					</label>
					<input
						type="number"
						min={0}
						value={sterilizationPayload.retained_duration_min}
						onKeyDown={(e) => {
							if (e.key === "-" || e.key === "e" || e.key === "E") {
								e.preventDefault();
							}
						}}
						onChange={(e) => { handlePayloadChange("retained_duration_min", e.target.value); setErrors((prev: any) => ({ ...prev, retainedDuration: "" })); }}
						disabled={isReadOnly}
						className={`${inputClassName} disabled:appearance-none`}
					/>
					{!isReadOnly && errors.retainedDuration && <p className="text-red-500 text-[11px]">{errors.retainedDuration}</p>}
				</div>
				<div className="space-y-2">
					<label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">
						{t("mediaBatchInit.sterilization.pressureReleaseTime")}  <span className="text-red-500">*</span>
					</label>
					<input
						type="time"
						value={sterilizationPayload.pressure_release_time}
						onChange={(e) => { handlePayloadChange("pressure_release_time", e.target.value); setErrors((prev: any) => ({ ...prev, pressureRelease: "" })); }}
						disabled={isReadOnly}
						className={`${inputClassName} disabled:appearance-none`}
					/>
					{!isReadOnly && errors.pressureRelease && <p className="text-red-500 text-[11px]">{errors.pressureRelease}</p>}
				</div>

				<div className="pt-8 border-t border-slate-100 space-y-4">
					<div className="bg-[#EEF6FF] border border-blue-100 rounded-2xl p-6">
						<div className="flex items-center gap-3 mb-3 text-blue-600">
							<span className="material-symbols-outlined text-sm font-black">info</span>
							<span className="text-[9px] font-black uppercase tracking-[0.2em]">
								{t("mediaBatchInit.sterilization.sopGuideLine")}
							</span>
						</div>
						<p className="text-[11px] text-blue-800 italic leading-relaxed font-medium">
							"{t("mediaBatchInit.sterilization.sopGuide")}"
						</p>
					</div>

					{isProcessDeviation && (
						<div className="bg-[#FFF5F5] border border-[#FEE2E2] rounded-lg p-6 animate-in fade-in slide-in-from-top-1">
							<div className="flex items-center gap-3 mb-3 text-[#B91C1C]">
								<span className="material-symbols-outlined text-sm font-black">report_problem</span>
								<span className="text-[9px] font-black uppercase tracking-[0.2em]">
									{t("mediaBatchInit.sterilization.criticalDeviationDetected")}
								</span>
							</div>
							<p className="text-[11px] text-[#991B1B] font-bold leading-relaxed">
								{t("mediaBatchInit.sterilization.deviationText")}
							</p>
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

export default SterilizationProcessEvents;
